import { useEffect, useRef, useState } from 'react';
import { API_BASE } from '../lib/api';
import { LANGUAGES } from '../lib/languages';
import { useDemoStore } from '../store';
import { CardScanSource } from './CardScanSource';
import { Icon, type IconName } from './Icon';

/**
 * R20 (U1) / R22 (K2) — how long a control affordance breathes after a pulse cue before it settles.
 * Shared by BOTH the Card pulse (`ui.pulse_card`, §23) and the generalized control pulse
 * (`ui.pulse_control`, §29 — the Type control today).
 * R40-hotfix: was 1.4s × 3 cycles (4.2s) — field session 5b9e09d3 proved the pulse settled BEFORE
 * Gigi finished saying "tap the pulsing Card button" (pulse fires at STT-final; speech lands seconds
 * later) → "there's no pulsing card button". Persistent flavour now; interaction still clears early.
 * (Kept in one place so the Card and Type pulses never drift; the CSS `.card-pulse` cycles infinite
 * and the host removes the class on this ceiling or on interaction.)
 */
const CONTROL_PULSE_MS = 30_000; // persistent flavour (design-language §8): the visitor MUST act — breathe until interaction or this generous ceiling

/**
 * R22 (K2) — the shared control-pulse effect. Generalizes the R20 card-pulse mechanics so ANY control
 * can breathe on a store nonce: watch the nonce CHANGE (skip the 0 initial value so it never fires on
 * mount), toggle a `pulsing` flag for the persistent window, and expose a `clear` the host calls on
 * interaction (once found, the nudge has done its job). A fresh nonce mid-settle re-arms cleanly (the
 * timer resets). Reduced-motion is handled in CSS (`.card-pulse` → a static ring), so this is a plain
 * timer either way (no motion), which is reduced-motion-safe by construction.
 */
function useControlPulse(nonce: number): { pulsing: boolean; clear: () => void } {
  const [pulsing, setPulsing] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const clear = () => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    setPulsing(false);
  };
  useEffect(() => {
    if (nonce === 0) return; // never pulsed yet — skip the initial value
    setPulsing(true);
    if (timerRef.current !== null) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      setPulsing(false);
    }, CONTROL_PULSE_MS);
    return () => {
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [nonce]);
  return { pulsing, clear };
}

/**
 * Voice control bar (shown once connected): mute, pause, type-a-turn, language (auto-detect chip +
 * dropdown to pin Gigi's reply language), business-card upload (with OCR prefill step), hang up.
 * Wired to useVoice actions.
 *
 * R14 B4: this bar lives in the STICKY region of the conversation panel (VoiceDemo) — it never
 * scrolls away mid-session.
 * R14 B5 → R23 C1: the Type button no longer opens/toggles an input — the chat composer is now
 * ALWAYS VISIBLE at the BOTTOM of the transcript region (VoiceDemo owns it). The bar's Type control
 * simply FOCUS/SCROLL-TO's that field (`onFocusComposer`) and stays the §31 pulse target.
 * R14 B6: the card button opens a Take photo / Photo library / Browse files source sheet (all three
 * feed the same OCR/upload flow) instead of jumping straight to a picker.
 */

type OcrFields = { name?: string; company?: string; email?: string; phone?: string };

export function ControlBar({
  micEnabled,
  paused,
  language,
  onMute,
  onPause,
  onFocusComposer,
  onSelectLanguage,
  onUpload,
  onPrefillLead,
  onHangUp,
  onScanSurfaceChange,
}: {
  micEnabled: boolean;
  paused: boolean;
  language: string;
  onMute: () => void;
  onPause: () => void;
  onFocusComposer: () => void;
  onSelectLanguage: (name: string) => void;
  onUpload: (f: File) => Promise<void>;
  onPrefillLead?: (fields: OcrFields) => void;
  onHangUp: () => void;
  /** R25 — card-scan surface open/close → host mutes the mic + emits the §38 `card-modal` signal. */
  onScanSurfaceChange?: (active: boolean) => void;
}) {
  const [showLangs, setShowLangs] = useState(false);
  const [uploadState, setUploadState] = useState<'idle' | 'busy' | 'done' | 'error'>('idle');
  const [ocrPrefill, setOcrPrefill] = useState<OcrFields | null>(null);
  const [pendingFile, setPendingFile] = useState<File | null>(null);

  // R20 (U1) → R23 (C3) — card-affordance pulse. Now unified into the §31 pulseable set: a
  // `ui.pulse_control {control:'card'}` cue (concierge R43 G2 — fires when Gigi HERSELF directs the
  // visitor to Card) OR the tolerated legacy `ui.pulse_card` alias both bump the `card` key of
  // `pulseControlNonce` (same nonce, one code path). The shared `useControlPulse` hook watches the
  // value change and breathes the Card control until interaction or the ceiling. `clearCardPulse`
  // clears it early on interaction — once the visitor's found it, the nudge has done its job.
  const cardPulseNonce = useDemoStore((s) => s.pulseControlNonce.card ?? 0);
  const { pulsing: cardPulsing, clear: clearCardPulse } = useControlPulse(cardPulseNonce);

  // R22 (K2) — Type-control pulse. A `ui.pulse_control {control:'type'}` cue (§31) bumps the Type
  // control's slot in `pulseControlNonce` when Gigi needs a field OUTSIDE the S7 flow ("tap Type above
  // and send me your email"). Same shared hook, same persistent brass-ring breathe as the Card pulse;
  // interaction (focusing the composer) clears it early. An absent slot is 0 → the hook never fires.
  const typePulseNonce = useDemoStore((s) => s.pulseControlNonce.type ?? 0);
  const { pulsing: typePulsing, clear: clearTypePulse } = useControlPulse(typePulseNonce);

  async function doUpload(f: File) {
    setUploadState('busy');
    try {
      await onUpload(f);
      setUploadState('done');
      setTimeout(() => setUploadState('idle'), 2500);
    } catch {
      setUploadState('error');
      setTimeout(() => setUploadState('idle'), 3000);
    }
  }

  async function onFile(f: File) {
    // Step 1: OCR extract BEFORE uploading to R2.
    // POST /v1/card/ocr — Butler R6 contract (card-ocr contract, event-and-floor-handoff-design.md §5).
    // Returns { name?, company?, email?, phone? }. 404/501 = not yet deployed → falls through.
    try {
      const ocrRes = await fetch(`${API_BASE}/v1/card/ocr`, {
        method: 'POST',
        headers: { 'content-type': f.type || 'application/octet-stream' },
        body: f,
      });
      if (ocrRes.ok) {
        const extracted = (await ocrRes.json()) as OcrFields;
        if (extracted.name || extracted.company || extracted.email || extracted.phone) {
          // Show prefill confirmation panel — don't upload yet
          setPendingFile(f);
          setOcrPrefill(extracted);
          return;
        }
      }
      // 404/501 = not yet live, or no fields extracted → fall through to direct upload
    } catch {
      // Non-fatal — if OCR fails, just upload directly
    }

    // Step 2: no OCR result or endpoint not live — upload directly
    await doUpload(f);
  }

  return (
    <div className="flex flex-col items-center gap-3 w-full">
      {ocrPrefill && (
        <OcrPrefillPanel
          prefill={ocrPrefill}
          onConfirm={(fields) => {
            setOcrPrefill(null);
            const f = pendingFile;
            setPendingFile(null);
            // Send prefill to Gigi via 'lead-prefill' RTVI client message (useVoice.prefillLead).
            // Butler R6 handles this in the agent (lead-prefill RTVI handler).
            onPrefillLead?.(fields);
            // Always upload to R2 after confirm
            if (f) void doUpload(f);
          }}
          onSkip={() => {
            setOcrPrefill(null);
            const f = pendingFile;
            setPendingFile(null);
            if (f) void doUpload(f);
          }}
        />
      )}

      {/* R27 — the 5 session tools group in one soft tray capsule (controls); Hang up is separated
          (exit) but joins the lift family. design-language §8 control-bar soft-UI. */}
      <div className="flex flex-wrap items-center justify-center gap-3">
        <div className="soft-tray flex flex-wrap items-center justify-center gap-1.5 p-1.5">
          <Ctl
            label={micEnabled ? 'Mute' : 'Unmute'}
            icon={micEnabled ? 'mic' : 'micOff'}
            active={!micEnabled}
            onClick={onMute}
          />
          <Ctl
            label={paused ? 'Resume' : 'Pause'}
            icon={paused ? 'play' : 'pause'}
            active={paused}
            breathing={paused}
            onClick={onPause}
          />
          {/* R23 C1 — Type FOCUSES the always-visible bottom-anchored chat composer (VoiceDemo owns it);
              it no longer opens/closes anything.
              R22 K2 — pulses a brass ring when Gigi points here on a `ui.pulse_control {control:'type'}`
              cue (§31) for the out-of-flow email/phone capture channel. Focusing the composer clears it. */}
          <Ctl
            label="Type"
            icon="keyboard"
            pulse={typePulsing}
            onClick={() => {
              clearTypePulse(); // focusing the composer answers the nudge
              onFocusComposer();
            }}
          />

          {/* Language: chip + dropdown */}
          <div className="relative">
            <Ctl
              label={language}
              icon="globe"
              active={showLangs}
              onClick={() => setShowLangs((s) => !s)}
            />
            {showLangs && (
              <div className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 z-[var(--z-overlay)] max-h-64 w-44 overflow-y-auto rounded-xl bg-elevated ring-1 ring-line-strong shadow-soft py-1">
                {LANGUAGES.map((l) => (
                  <button
                    key={l.code}
                    type="button"
                    onClick={() => {
                      onSelectLanguage(l.name);
                      setShowLangs(false);
                    }}
                    className={`block w-full text-left px-3 py-1.5 text-sm hover:bg-fill ${
                      l.name === language ? 'text-brass' : 'text-ink'
                    }`}
                  >
                    {l.name}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* R14 B6 — card scan offers a Take photo / Photo library / Browse files source sheet.
              R20 U1 — pulses a brass ring when Gigi points here on a `ui.pulse_card` cue (§23). */}
          <CardScanSource
            onFile={(f) => void onFile(f)}
            title="Scan a card from"
            onScanSurfaceChange={onScanSurfaceChange}
          >
            {({ open }) => (
              <Ctl
                label={
                  uploadState === 'busy'
                    ? 'Uploading…'
                    : uploadState === 'done'
                      ? 'Card saved'
                      : uploadState === 'error'
                        ? 'Failed'
                        : 'Card'
                }
                icon="idCard"
                active={uploadState === 'busy'}
                pulse={cardPulsing}
                onClick={() => {
                  clearCardPulse(); // once found, the nudge has done its job
                  open();
                }}
              />
            )}
          </CardScanSource>
        </div>

        <Ctl label="Hang up" icon="x" danger onClick={onHangUp} />
      </div>
    </div>
  );
}

function OcrPrefillPanel({
  prefill,
  onConfirm,
  onSkip,
}: {
  prefill: OcrFields;
  onConfirm: (fields: OcrFields) => void;
  onSkip: () => void;
}) {
  const [fields, setFields] = useState<OcrFields>(prefill);
  return (
    <div className="w-full max-w-md rounded-xl bg-surface ring-1 ring-line p-4 flex flex-col gap-3 shadow-soft">
      <p className="text-sm font-display text-ink">We found these details — confirm to prefill</p>
      <div className="flex flex-col gap-2">
        {(['name', 'company', 'email', 'phone'] as const).map((field) =>
          fields[field] !== undefined ? (
            <div key={field} className="flex flex-col gap-0.5">
              <label
                htmlFor={`ocr-field-${field}`}
                className="text-[10px] uppercase tracking-wide text-faint"
              >
                {field}
              </label>
              <input
                id={`ocr-field-${field}`}
                value={fields[field] ?? ''}
                onChange={(e) => setFields((prev) => ({ ...prev, [field]: e.target.value }))}
                className="rounded-md bg-fill ring-1 ring-line px-2.5 py-1.5 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-brass"
              />
            </div>
          ) : null
        )}
      </div>
      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => onConfirm(fields)}
          className="btn-primary flex-1 py-2 text-sm"
        >
          Use these
        </button>
        <button type="button" onClick={onSkip} className="btn-ghost flex-1 py-2 text-sm">
          Skip
        </button>
      </div>
    </div>
  );
}

function Ctl({
  label,
  icon,
  active,
  danger,
  breathing,
  pulse,
  onClick,
}: {
  label: string;
  icon: IconName;
  active?: boolean;
  danger?: boolean;
  /** R27 — engaged tool that should slow-breathe its brass glow (Pause = "we're on hold"). */
  breathing?: boolean;
  /** R20 U1 — draw the eye with an auto-settling brass-ring breathe (`.card-pulse`, §23). */
  pulse?: boolean;
  onClick: () => void;
}) {
  // R27 soft-UI: every control lifts (elev-lift) and sinks on press (soft-lift). An ENGAGED tool
  // (Mute-on / Pause-on) layers the brass inner-glow (`tool-engaged`), Pause additionally breathing.
  // Danger (Hang up) keeps its terracotta identity but joins the lift family. Bare bg so the tray's
  // recess reads behind each button; ≥44px min target (min-h-14/min-w-16 → 56×64px) for kiosk touch.
  const engaged = !!active && !danger;
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex min-h-14 min-w-16 flex-col items-center justify-center gap-1 rounded-xl bg-surface px-3 py-2 text-[11px] transition-colors duration-1 ease-physical soft-lift ${
        pulse ? 'card-pulse' : ''
      } ${engaged ? 'tool-engaged' : ''} ${breathing && engaged ? 'tool-breathing' : ''} ${
        danger
          ? 'text-terracotta hover:bg-terracotta/10'
          : engaged
            ? 'text-brass'
            : 'text-subtle hover:text-ink'
      }`}
      aria-pressed={active}
    >
      <Icon name={icon} size={18} />
      <span className="max-w-20 truncate">{label}</span>
    </button>
  );
}
